import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../hooks/useProjectStore';
import { useAuthStore } from '../hooks/useAuthStore';
import CreateProjectModal from '../components/common/CreateProjectModal';
import { useLangStore, useT } from '../i18n';
import LanguageSwitch from '../components/common/LanguageSwitch';

export default function ProjectSelectPage() {
  const navigate = useNavigate();
  const { projects, loading, fetchProjects, setCurrentProject } = useProjectStore();
  const { user, logout } = useAuthStore();
  const t = useT();
  const lang = useLangStore((s) => s.lang);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => { fetchProjects(); }, []);

  const handleSelect = (project: any) => {
    setCurrentProject(project);
    navigate('/dashboard');
  };

  const statusMap: Record<string, { label: string; color: string; bg: string }> = {
    draft:     { label: '草稿', color: '#8598AD', bg: '#F0F3F6' },
    active:    { label: '进行中', color: '#27864A', bg: '#E8F5ED' },
    completed: { label: '已完成', color: '#2874A6', bg: '#E8F0F8' },
    archived:  { label: '已归档', color: '#8598AD', bg: '#F0F3F6' },
  };

  const wellTypeIcon: Record<string, string> = {
    '水平井': '↗',
    '定向井': '↘',
    '直井': '↓',
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#FFFFFF',
      fontFamily: 'inherit',
    }}>
      {/* Header */}
      <div style={{
        padding: '24px 40px', background: '#FFFFFF',
        borderBottom: '1px solid #D6DAE0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 28, color: '#C2533A' }}>⬡</span>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1E2A3A', letterSpacing: 1 }}>FracSight</div>
            <div style={{ fontSize: 11, color: '#8598AD', letterSpacing: 2 }}>{t('石油压裂智能平台')}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <LanguageSwitch style={{ marginRight: 8 }} />
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: '#3D5168', color: '#A8B8C8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700,
          }}>
            {user?.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1E2A3A' }}>{user?.username || t('用户')}</div>
            <div style={{ fontSize: 11, color: '#8598AD' }}>{user?.company || ''}</div>
          </div>
          <button
            onClick={handleLogout}
            style={{
              marginLeft: 12, padding: '6px 14px', borderRadius: 6,
              border: '1px solid #D6DAE0', background: '#FFFFFF',
              color: '#4F6680', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#C2533A';
              e.currentTarget.style.color = '#C2533A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#D6DAE0';
              e.currentTarget.style.color = '#4F6680';
            }}
          >
            {t('退出登录')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E2A3A', margin: '0 0 6px 0' }}>
            {t('选择项目')}
          </h1>
          <p style={{ fontSize: 14, color: '#8598AD', margin: 0 }}>
            {t('选择一个项目开始工作，或创建新项目')}
          </p>
        </div>

        {/* New Project Button */}
        <div
          onClick={() => setCreateOpen(true)}
          style={{
            padding: '20px 24px', marginBottom: 16,
            background: '#FFFFFF', border: '2px dashed #D6DAE0',
            borderRadius: 10, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 14,
            transition: 'border-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#C2533A')}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#D6DAE0')}
        >
          <div style={{
            width: 48, height: 48, borderRadius: 8,
            background: 'rgba(194,83,58,0.06)', color: '#C2533A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, fontWeight: 300,
          }}>+</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1E2A3A' }}>{t('新建项目')}</div>
            <div style={{ fontSize: 12, color: '#8598AD' }}>{t('创建一个新的压裂项目')}</div>
          </div>
        </div>

        {/* Project List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#8598AD' }}>{t('加载中...')}</div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#8598AD' }}>
            {t('暂无项目，点击上方创建')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {projects.map((p) => {
              const st = statusMap[p.status] || statusMap.draft;
              return (
                <div
                  key={p.id}
                  onClick={() => handleSelect(p)}
                  style={{
                    padding: '20px 24px',
                    background: '#FFFFFF',
                    border: '1px solid #D6DAE0',
                    borderRadius: 10,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    transition: 'all 0.15s',
                    boxShadow: '0 1px 3px rgba(30,42,58,0.04)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#C2533A';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(194,83,58,0.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#D6DAE0';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(30,42,58,0.04)';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  {/* Well type icon */}
                  <div style={{
                    width: 52, height: 52, borderRadius: 10,
                    background: '#F4F6F9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, color: '#4F6680', fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {wellTypeIcon[p.well_type] || '◆'}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#1E2A3A' }}>{p.well_name}</span>
                      <span style={{ fontSize: 13, color: '#4F6680' }}>{p.name}</span>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 4,
                        background: st.bg, color: st.color, fontWeight: 600,
                      }}>{t(st.label)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#8598AD' }}>
                      <span>{p.field_name}</span>
                      {p.well_type && <span>{t(p.well_type)}</span>}
                      {p.target_formation && <span>{t('目标层: {v}', { v: p.target_formation })}</span>}
                      {p.total_depth && <span>{t('总深: {v}m', { v: p.total_depth })}</span>}
                    </div>
                  </div>

                  {/* Meta */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: '#8598AD' }}>{t('{n} 人', { n: p.member_count || 1 })}</div>
                    <div style={{ fontSize: 11, color: '#C8D3DE' }}>
                      {p.updated_at ? new Date(p.updated_at).toLocaleDateString(lang === 'en' ? 'en-US' : 'zh-CN') : ''}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div style={{ color: '#C8D3DE', fontSize: 18, flexShrink: 0 }}>→</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateProjectModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(p) => { setCurrentProject(p); navigate('/dashboard'); }}
      />
    </div>
  );
}
